import React, { useEffect, useMemo, useState } from 'react';
import { useParams, Navigate, Link } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { AdminPageHeader } from '@/components/admin/AdminPageHeader';
import { EmptyState } from '@/components/shared/EmptyState';
import { storage, formatDate } from '@/lib/storage';
import { CONTENT_TYPES } from '@/data/contentTypes';
import { Plus, Search, Edit, Trash2, Pin, Image as ImageIcon, FolderOpen } from 'lucide-react';
import { toast } from 'sonner';

const blankFromSchema = (fields) => fields.reduce((acc, f) => {
  if (f.type === 'switch') acc[f.name] = false;
  else if (f.type === 'number') acc[f.name] = 0;
  else acc[f.name] = '';
  return acc;
}, {});

const ManageContent = () => {
  const { type } = useParams();
  const cfg = CONTENT_TYPES[type];

  const [items, setItems] = useState([]);
  const [q, setQ] = useState('');
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!cfg) return;
    let stored = storage.get(cfg.storageKey, null);
    if (stored === null) {
      stored = cfg.seed.map((s) => ({ ...s }));
      storage.set(cfg.storageKey, stored);
    }
    setItems(stored);
    setQ('');
    setEditing(null);
    setForm({});
  }, [type, cfg]);

  const filtered = useMemo(() => {
    if (!cfg) return [];
    return items.filter((it) => Object.values(it).join(' ').toLowerCase().includes(q.toLowerCase()));
  }, [items, q, cfg]);

  if (!cfg) return <Navigate to="/admin" replace />;

  const openCreate = () => {
    setEditing(null);
    setForm(blankFromSchema(cfg.fields));
    setOpen(true);
  };

  const openEdit = (item) => {
    setEditing(item);
    setForm({ ...item });
    setOpen(true);
  };

  const save = (e) => {
    e.preventDefault();
    for (const f of cfg.fields) {
      if (f.required && !form[f.name]) {
        return toast.error(`${f.label} is required.`);
      }
    }
    const updated = [...items];
    if (editing) {
      const idx = updated.findIndex((x) => x.id === editing.id);
      updated[idx] = { ...editing, ...form };
    } else {
      const newItem = {
        id: `cms-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
        ...form,
      };
      updated.unshift(newItem);
    }
    storage.set(cfg.storageKey, updated);
    setItems(updated);
    setOpen(false);
    toast.success(editing ? `${cfg.singular} updated` : `${cfg.singular} created`);
  };

  const remove = (id) => {
    if (!window.confirm(`Delete this ${cfg.singular.toLowerCase()}?`)) return;
    const next = items.filter((x) => x.id !== id);
    storage.set(cfg.storageKey, next);
    setItems(next);
    toast.success(`${cfg.singular} deleted`);
  };

  return (
    <div data-testid={`admin-content-${type}-page`}>
      <AdminPageHeader
        title={cfg.label}
        description={`Create, edit, and remove ${cfg.label.toLowerCase()} shown on the public site.`}
        actions={
          <>
            <Button asChild variant="outline" data-testid="content-public-link">
              <Link to={`/${type === 'accomplishments' ? 'accomplishments' : type}`} target="_blank">View public page</Link>
            </Button>
            <Button onClick={openCreate} className="bg-gradient-primary text-primary-foreground" data-testid="content-create">
              <Plus className="mr-2 h-4 w-4" /> New {cfg.singular}
            </Button>
          </>
        }
      />

      <Card className="p-4 sm:p-5 mb-5 border-0 shadow-sm-soft">
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={`Search ${cfg.label.toLowerCase()}...`} className="pl-9" data-testid="content-search" />
        </div>
      </Card>

      <Card className="border-0 shadow-md-soft overflow-hidden">
        {filtered.length === 0 ? (
          <div className="p-6">
            <EmptyState
              icon={FolderOpen}
              title="No items yet"
              description={`Click "New ${cfg.singular}" to create your first entry.`}
              action={<Button onClick={openCreate} className="bg-gradient-primary text-primary-foreground"><Plus className="mr-2 h-4 w-4" /> New {cfg.singular}</Button>}
            />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  {cfg.columns.map((col) => (
                    <TableHead key={col} className="capitalize">{col}</TableHead>
                  ))}
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((it) => (
                  <TableRow key={it.id} data-testid={`row-content-${it.id}`}>
                    {cfg.columns.map((col) => (
                      <TableCell key={col}>{renderCell(it, col)}</TableCell>
                    ))}
                    <TableCell className="text-right">
                      <Button size="icon" variant="ghost" onClick={() => openEdit(it)} data-testid={`edit-content-${it.id}`}>
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button size="icon" variant="ghost" onClick={() => remove(it.id)} className="text-destructive hover:bg-destructive/10 hover:text-destructive" data-testid={`delete-content-${it.id}`}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display">
              {editing ? `Edit ${cfg.singular}` : `New ${cfg.singular}`}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={save} className="grid sm:grid-cols-2 gap-4 mt-2">
            {cfg.fields.map((f) => (
              <div key={f.name} className={f.full ? 'sm:col-span-2' : ''}>
                <Label>{f.label}{f.required && ' *'}</Label>
                {f.type === 'textarea' ? (
                  <Textarea rows={f.rows || 3} value={form[f.name] || ''} onChange={(e) => setForm({ ...form, [f.name]: e.target.value })} placeholder={f.placeholder} data-testid={`form-${f.name}`} />
                ) : f.type === 'switch' ? (
                  <div className="flex items-center gap-3 mt-2">
                    <Switch checked={Boolean(form[f.name])} onCheckedChange={(v) => setForm({ ...form, [f.name]: v })} data-testid={`form-${f.name}`} />
                    <span className="text-sm text-muted-foreground">{form[f.name] ? 'On' : 'Off'}</span>
                  </div>
                ) : f.type === 'select' ? (
                  <Select value={form[f.name] || ''} onValueChange={(v) => setForm({ ...form, [f.name]: v })}>
                    <SelectTrigger data-testid={`form-${f.name}`}><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>
                      {f.options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                    </SelectContent>
                  </Select>
                ) : (
                  <Input
                    type={f.type === 'date' ? 'date' : f.type === 'number' ? 'number' : 'text'}
                    value={form[f.name] ?? ''}
                    onChange={(e) => setForm({ ...form, [f.name]: f.type === 'number' ? Number(e.target.value) : e.target.value })}
                    placeholder={f.placeholder}
                    data-testid={`form-${f.name}`}
                  />
                )}
              </div>
            ))}
            <DialogFooter className="sm:col-span-2 mt-2">
              <Button type="button" variant="outline" onClick={() => setOpen(false)} data-testid="form-cancel">Cancel</Button>
              <Button type="submit" className="bg-gradient-primary text-primary-foreground" data-testid="form-save">
                {editing ? 'Save changes' : 'Create'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const renderCell = (item, col) => {
  const v = item[col];
  if (typeof v === 'boolean') return v ? <Badge className="bg-warning/15 text-warning border-warning/30 hover:bg-warning/25"><Pin className="h-3 w-3 mr-1" /> Yes</Badge> : <span className="text-muted-foreground text-xs">—</span>;
  if (col === 'date' || /Date$/.test(col)) return formatDate(v);
  if (typeof v === 'string' && v.length > 80) return <span title={v}>{v.slice(0, 80)}…</span>;
  if (col === 'photo' || col === 'image') return v ? <img src={v} alt="" className="h-8 w-8 rounded object-cover" /> : <ImageIcon className="h-4 w-4 text-muted-foreground" />;
  return v || <span className="text-muted-foreground text-xs">—</span>;
};

export default ManageContent;
